// src/context/SSEContext.jsx
import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from './useAuth';

const SSEContext = createContext(null);

const CONNECTION_STATUS = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
};

// Max reconnect delay: 30 seconds
const MAX_RECONNECT_DELAY = 30000;
const BASE_RECONNECT_DELAY = 1000;

// Map stream URLs to their token-exchange endpoints.
// Each SSE endpoint has a sibling POST endpoint that issues a scoped 5-min token.
const TOKEN_ENDPOINT_MAP = {
  '/api/v1/balance/events': '/api/v1/balance/sse-token',
  // Workflow URLs are dynamic: /api/v1/workflows/{id}/events -> /api/v1/workflows/{id}/sse-token
};

function resolveTokenEndpoint(streamUrl) {
  // Check static map first
  if (TOKEN_ENDPOINT_MAP[streamUrl]) {
    return TOKEN_ENDPOINT_MAP[streamUrl];
  }
  // Dynamic: replace /events suffix with /sse-token
  if (streamUrl.endsWith('/events')) {
    return streamUrl.replace(/\/events$/, '/sse-token');
  }
  return null;
}

export function SSEProvider({ children }) {
  const { token, isAuthenticated } = useAuth();
  const [connectionStatus, setConnectionStatus] = useState(CONNECTION_STATUS.DISCONNECTED);

  // Map of event type -> Set of listener callbacks
  const listenersRef = useRef(new Map());
  // Map of URL -> EventSource instance
  const sourcesRef = useRef(new Map());
  // Track reconnect attempts per URL for exponential backoff
  const reconnectAttemptsRef = useRef(new Map());
  // Track reconnect timers for cleanup
  const reconnectTimersRef = useRef(new Map());
  // Track if provider is mounted
  const mountedRef = useRef(true);
  // Latest connectToSource, so timers/handlers never capture a stale closure.
  const connectRef = useRef(null);
  // URLs the app currently wants a stream for (set on connect, cleared on
  // disconnect), so a failed token exchange never resurrects a dropped stream.
  const wantedRef = useRef(new Set());
  // URL -> number of consumers holding the stream open. The stream is shared;
  // it only closes when the last consumer disconnects.
  const refCountsRef = useRef(new Map());
  // The access token is only used as an Authorization header by the axios
  // default; here it is read through a ref so that a token refresh alone does
  // not change callback identities and rebuild every open stream. Streams use
  // short sse-tokens exchanged at connect time.
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const closeAllSources = useCallback(() => {
    for (const [url, source] of sourcesRef.current.entries()) {
      source.close();
    }
    sourcesRef.current.clear();
    wantedRef.current.clear();
    refCountsRef.current.clear();
    reconnectAttemptsRef.current.clear();
    for (const timer of reconnectTimersRef.current.values()) {
      clearTimeout(timer);
    }
    reconnectTimersRef.current.clear();
    setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
  }, []);

  const dispatchEvent = useCallback((eventType, data) => {
    const listeners = listenersRef.current.get(eventType);
    if (listeners) {
      for (const listener of listeners) {
        try {
          listener(data);
        } catch (err) {
          console.error(`SSE listener error for "${eventType}":`, err);
        }
      }
    }
    // Also dispatch to wildcard listeners
    const wildcardListeners = listenersRef.current.get('*');
    if (wildcardListeners) {
      for (const listener of wildcardListeners) {
        try {
          listener({ type: eventType, data });
        } catch (err) {
          console.error('SSE wildcard listener error:', err);
        }
      }
    }
  }, []);

  // Retry connecting a URL after a delay (exponential backoff). Used both after
  // a stream error and when the sse-token exchange itself failed.
  const scheduleReconnect = useCallback((url) => {
    const attempts = (reconnectAttemptsRef.current.get(url) || 0) + 1;
    reconnectAttemptsRef.current.set(url, attempts);
    const delay = Math.min(
      BASE_RECONNECT_DELAY * Math.pow(2, attempts - 1) + Math.random() * 1000,
      MAX_RECONNECT_DELAY
    );
    setConnectionStatus(CONNECTION_STATUS.RECONNECTING);
    clearTimeout(reconnectTimersRef.current.get(url));
    const timer = setTimeout(() => {
      reconnectTimersRef.current.delete(url);
      // Only reconnect once a (fresh) token is available.
      if (!wantedRef.current.has(url)) return;
      if (mountedRef.current && tokenRef.current && connectRef.current) {
        connectRef.current(url);
      } else if (mountedRef.current && sourcesRef.current.size === 0) {
        setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
      }
    }, delay);
    reconnectTimersRef.current.set(url, timer);
  }, []);

  const openEventSource = useCallback((url, sseToken) => {
    const separator = url.includes('?') ? '&' : '?';
    const fullUrl = `${url}${separator}token=${encodeURIComponent(sseToken)}`;

    const source = new EventSource(fullUrl);
    sourcesRef.current.set(url, source);

    source.onopen = () => {
      if (!mountedRef.current) return;
      reconnectAttemptsRef.current.set(url, 0);
      setConnectionStatus(CONNECTION_STATUS.CONNECTED);
    };

    source.onerror = () => {
      if (!mountedRef.current) return;

      // EventSource auto-reconnects on transient errors, but its built-in
      // reconnect would reuse the same (possibly expired) SSE token in the URL.
      // We close it and reconnect manually with a fresh token instead.
      source.close();
      sourcesRef.current.delete(url);

      scheduleReconnect(url);
    };

    // Named events are dispatched via addEventListener, not onmessage.
    const knownEvents = [
      // Workflow events (roleserver)
      'snapshot',
      'workflow.node.started',
      'workflow.node.completed',
      'workflow.node.failed',
      'workflow.completed',
      'workflow.failed',
      'workflow.status_changed',
      'workflow.paused',
      // Balance events (billingservice)
      'balance.reserved',
      'balance.consumed',
      'balance.released',
      'balance.finalized',
      'balance.topup',
    ];

    for (const eventType of knownEvents) {
      source.addEventListener(eventType, (e) => {
        if (!mountedRef.current) return;
        try {
          const data = JSON.parse(e.data);
          dispatchEvent(eventType, data);
        } catch (err) {
          console.error(`SSE parse error for "${eventType}":`, err);
        }
      });
    }

    return source;
  }, [dispatchEvent, scheduleReconnect]);

  const openStream = useCallback(async (url) => {
    if (!tokenRef.current || sourcesRef.current.has(url)) return;
    wantedRef.current.add(url);

    setConnectionStatus(CONNECTION_STATUS.CONNECTING);

    // Fetch a short-lived SSE token via the token-exchange endpoint.
    // The Bearer JWT is sent in the Authorization header (safe, not in URL).
    const tokenEndpoint = resolveTokenEndpoint(url);
    if (!tokenEndpoint) {
      console.error(`No SSE token endpoint mapped for ${url}`);
      return;
    }

    try {
      const res = await axios.post(tokenEndpoint);
      const sseToken = res.data.token;
      if (!mountedRef.current) return;
      openEventSource(url, sseToken);
    } catch (err) {
      console.error('Failed to fetch SSE token:', err);
      if (!mountedRef.current) return;
      // The Bearer may have just expired (the axios refresh interceptor
      // handles that transparently); otherwise retry on the normal backoff
      // instead of staying DISCONNECTED forever.
      scheduleReconnect(url);
    }
  }, [openEventSource, scheduleReconnect, isAuthenticated]);

  // Internal (uncounted) connect, also used by reconnect timers.
  connectRef.current = openStream;

  const connectToSource = useCallback((url) => {
    if (!tokenRef.current) return undefined;
    const count = (refCountsRef.current.get(url) || 0) + 1;
    refCountsRef.current.set(url, count);
    if (count > 1) return undefined;
    return openStream(url);
  }, [openStream]);

  const disconnectFromSource = useCallback((url) => {
    const count = refCountsRef.current.get(url) || 0;
    if (count > 1) {
      refCountsRef.current.set(url, count - 1);
      return;
    }
    refCountsRef.current.delete(url);
    wantedRef.current.delete(url);
    const source = sourcesRef.current.get(url);
    if (source) {
      source.close();
      sourcesRef.current.delete(url);
    }
    const timer = reconnectTimersRef.current.get(url);
    if (timer) {
      clearTimeout(timer);
      reconnectTimersRef.current.delete(url);
    }
    reconnectAttemptsRef.current.delete(url);

    // Update status if no sources remain
    if (sourcesRef.current.size === 0) {
      setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
    }
  }, []);

  const subscribe = useCallback((eventType, callback) => {
    if (!listenersRef.current.has(eventType)) {
      listenersRef.current.set(eventType, new Set());
    }
    listenersRef.current.get(eventType).add(callback);

    // Return unsubscribe function
    return () => {
      const listeners = listenersRef.current.get(eventType);
      if (listeners) {
        listeners.delete(callback);
        if (listeners.size === 0) {
          listenersRef.current.delete(eventType);
        }
      }
    };
  }, []);

  // Close all connections on logout
  useEffect(() => {
    if (!isAuthenticated) {
      closeAllSources();
    }
  }, [isAuthenticated, closeAllSources]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      closeAllSources();
    };
  }, [closeAllSources]);

  const value = {
    connectionStatus,
    subscribe,
    connectToSource,
    disconnectFromSource,
    closeAllSources,
  };

  return (
    <SSEContext.Provider value={value}>
      {children}
    </SSEContext.Provider>
  );
}

export default SSEContext;
